import {groupBy, mapObject} from '@benchling/js-utils';

import {AssemblySelector} from 'molbio/bulk-assembly/AssemblySelector';
import {
  BulkAssemblyBin,
  BulkAssemblyBinType,
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  ErrorsByBin,
  FragmentProductionMethod,
  getMaxFragmentBinCount,
  isBulkAssemblyFragmentBin,
  isBulkAssemblySpacerBin,
  LayoutValidationStatus,
  BulkAssemblyTopology,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BulkAssemblyLayoutError} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {BulkAssemblyConstructsCardError} from 'molbio/bulk-assembly/card/BulkAssemblyCard';

export class AssemblyLayoutValidator {
  assemblySelector: AssemblySelector;

  constructor(readonly state: BulkAssemblyProcedure) {
    this.assemblySelector = new AssemblySelector(state);
  }

  /**
   * Get the bin at the given index in the assembly layout
   * @param {number} index The index at which to get the bin.
   */
  getNthBin(index: number): BulkAssemblyBin {
    return this.state.binsByCid[this.state.binCidOrdering[index]];
  }

  /**
   * Given an index, get the bin at the previous index in the assembly, accounting for assembly topology.
   * @param {number} index The index to get the previous bin for.
   */
  getPreviousBin(index: number): BulkAssemblyBin | null {
    if (index > 0) {
      return this.getNthBin(index - 1);
    } else if (this.state.topology === BulkAssemblyTopology.CIRCULAR) {
      return this.getNthBin(this.state.binCidOrdering.length - 1);
    } else {
      return null;
    }
  }

  /**
   * Given an index, get the bin at the next index in the assembly, accounting for assembly topology.
   * @param {number} index The index to get the next bin for.
   */
  getNextBin(index: number): BulkAssemblyBin | null {
    if (index < this.state.binCidOrdering.length - 1) {
      return this.getNthBin(index + 1);
    } else if (this.state.topology === BulkAssemblyTopology.CIRCULAR) {
      return this.getNthBin(0);
    } else {
      return null;
    }
  }

  /**
   * Given a fragment bin, checks whether that bin can be adjacent to a spacer in the assembly layout.
   * @param {BulkAssemblyBin | null } bin An optional fragment bin.
   */
  binCanBeAdjacentToSpacer(bin: BulkAssemblyBin | null): boolean {
    // bin == null case is a linear assembly where we're looking at bins adjacent to the first or last
    // bin in the assembly.
    // In the case of a spacer bin, no it can't be next to another spacer, but we have a separate error
    // for that
    return (
      bin == null ||
      bin.binType === BulkAssemblyBinType.SPACER ||
      [FragmentProductionMethod.PRIMER_PAIR, FragmentProductionMethod.SLICE].includes(
        bin.fragmentProductionMethod
      )
    );
  }

  /**
   * Checks whether the assembly layout is valid, returning a list of errors per assembly bin.
   */
  getAssemblyLayoutValidationErrors(): LayoutValidationStatus {
    const errorsByBin: ErrorsByBin = mapObject(this.state.binsByCid, () => new Set());
    const otherErrors: Array<BulkAssemblyConstructsCardError> = [];

    let numExistingCutSitesBins = 0;

    for (const [i, binCid] of this.state.binCidOrdering.entries()) {
      const bin = this.state.binsByCid[binCid];

      if (
        isBulkAssemblyFragmentBin(bin) &&
        bin.fragmentProductionMethod === FragmentProductionMethod.EXISTING_CUT_SITES
      ) {
        numExistingCutSitesBins++;
      }

      // TODO(BNCH-67581): allow adjacent spacers for Concatenation
      if (isBulkAssemblySpacerBin(bin)) {
        // If we're making circular constructs, this becomes the last card in the array at the first iteration
        const prevBin = this.getPreviousBin(i);
        // If we're making circular constructs, this becomes the first card in the array at the last iteration
        const nextBin = this.getNextBin(i);

        if (
          (prevBin != null && isBulkAssemblySpacerBin(prevBin)) ||
          (nextBin != null && isBulkAssemblySpacerBin(nextBin))
        ) {
          // Spacer cards can't be next to each other; set error if they are.
          errorsByBin[binCid].add(BulkAssemblyLayoutError.ADJACENT_SPACERS);
        }

        // No real restrictions to enforce on concatenation spacers.
        if (this.state.method.type !== BulkAssemblyMethod.CONCATENATION) {
          // Spacer cards have to be next to two bins that use primers; set errors if they aren't.
          if (
            (prevBin != null && isBulkAssemblyFragmentBin(prevBin)) ||
            (nextBin != null && isBulkAssemblyFragmentBin(nextBin))
          ) {
            // If the spacer is adjacent to two bins with the wrong fragment production method, set the error on
            // the spacer bin.
            if (!this.binCanBeAdjacentToSpacer(prevBin) && !this.binCanBeAdjacentToSpacer(nextBin)) {
              errorsByBin[binCid].add(BulkAssemblyLayoutError.SPACERS_NEED_PRIMERS);
            }
          }

          if (this.state.topology === BulkAssemblyTopology.LINEAR && (prevBin == null || nextBin == null)) {
            errorsByBin[binCid].add(BulkAssemblyLayoutError.TERMINAL_LINEAR_SPACERS);
          }
        }
      }
    }

    const fragmentBins = Object.values(this.state.binsByCid).filter(
      (bin: BulkAssemblyFragmentBin) => bin.binType === BulkAssemblyBinType.FRAGMENT
    );
    const fragmentBinsByName = groupBy(fragmentBins, (bin: BulkAssemblyBin) => bin.name);
    Object.values(fragmentBinsByName).forEach((binsOfName: Array<BulkAssemblyBin>) => {
      if (binsOfName.length > 1) {
        for (const bin of binsOfName) {
          errorsByBin[bin.cid].add(BulkAssemblyLayoutError.DUPLICATE_BIN_NAME);
        }
      }
    });

    if (this.assemblySelector.getMethod().type === BulkAssemblyMethod.GIBSON && numExistingCutSitesBins > 1) {
      otherErrors.push(BulkAssemblyLayoutError.TOO_MANY_EXISTING_CUTSITES_BINS_FOR_GIBSON);
    }

    if (fragmentBins.length > getMaxFragmentBinCount()) {
      otherErrors.push(BulkAssemblyLayoutError.TOO_MANY_BINS);
    }

    return {errorsByBin, otherErrors};
  }
}
